import { PureInput, InputProps, PureInputType } from './PureInput';
import { commify, removeNonNumeric } from '@services/MarppleShop/shared/util/commify.ts';

export interface NumberTextInputType extends PureInputType {
  getNumericValue(): number | null;
}

export interface NumberTextInputProps extends Omit<InputProps, 'type' | 'value' | 'onFocusout'> {
  value?: number | null;
  min?: number;
  max?: number;
  onFocusout?: (view: NumberTextInputType) => void;
}

export class NumberTextInput extends PureInput implements NumberTextInputType {
  private readonly min: number;
  private readonly max: number;

  constructor(data: NumberTextInputProps) {
    const originalOnInput = data.onInput;
    const originalOnFocusout = data.onFocusout;

    const { rows, ...pureInputData } = data;

    // PureInput의 생성자로 전달할 props 구성
    const inputProps: InputProps = {
      ...pureInputData,
      type: 'text',
      value: data.value === null || data.value === undefined ? '' : commify(data.value.toString()),
      onInput: (view) => {
        // 숫자 입력 처리 로직
        this.handleNumberTextInput(view);

        // 기존 onInput이 있으면 호출
        if (originalOnInput) {
          originalOnInput(view);
        }
      },
      onFocusout: (view) => {
        this.handleFocusout(view);

        if (originalOnFocusout) {
          originalOnFocusout(this);
        }
      },
    };

    super(inputProps);

    this.min = data.min !== undefined ? data.min : Number.MIN_SAFE_INTEGER;
    this.max = data.max !== undefined ? data.max : Number.MAX_SAFE_INTEGER;
  }

  private handleNumberTextInput(view: PureInputType): void {
    const inputValue = view.getValue();
    const numericValue = removeNonNumeric(inputValue);

    // 빈 문자열이면 그대로 유지 (포커스아웃에서 처리)
    if (numericValue === '') {
      if (inputValue !== '') {
        view.setValue('');
      }
      return;
    }

    let numValue = parseInt(numericValue, 10);

    // NaN 체크
    if (isNaN(numValue)) {
      view.setValue('');
      return;
    }

    // 범위 체크
    if (numValue < this.min) {
      numValue = this.min;
    } else if (numValue > this.max) {
      numValue = this.max;
    }

    // 천단위 콤마 형식화
    const formattedValue = commify(numValue.toString());

    // 값이 변경되었으면 업데이트
    if (formattedValue !== inputValue) {
      view.setValue(formattedValue);
    }
  }

  private handleFocusout(view: PureInputType): void {
    const value = view.getValue();

    // 빈 값이고 placeholder가 없는 경우에만 기본값 설정
    if ((value === '' || removeNonNumeric(value) === '') && !this.data.placeholder) {
      let defaultValue = 0;

      // 0이 min보다 작으면 min 값 사용
      if (defaultValue < this.min) {
        defaultValue = this.min;
      }

      view.setValue(commify(defaultValue.toString()));
    }
  }

  getNumericValue(): number | null {
    const value = this.getValue();
    if (value === '') return null;

    const numericValue = removeNonNumeric(value);
    if (numericValue === '') return null;

    const numValue = parseInt(numericValue, 10);
    return isNaN(numValue) ? null : numValue;
  }

  override setValue(value: number | string | null): this {
    if (value === null || value === undefined) {
      super.setValue('');
      return this;
    }

    let numValue: number;

    if (typeof value === 'string') {
      const numericValue = removeNonNumeric(value);
      if (numericValue === '') {
        super.setValue('');
        return this;
      }
      numValue = parseInt(numericValue, 10);
      if (isNaN(numValue)) {
        super.setValue('');
        return this;
      }
    } else {
      numValue = value;
    }

    if (numValue < this.min) {
      numValue = this.min;
    } else if (numValue > this.max) {
      numValue = this.max;
    }

    super.setValue(commify(numValue.toString()));
    return this;
  }
}
